import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { ConfirmationModel } from "../../components/features/common";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../components/UI/Declarations";
import { PagedList } from "../../components/UI/PagedList/PagedList";
import { sourceActivitiesService } from "../../services/reference/sourceActivities.service";
import { addEntityActionLabel, buildRecordLifecycleConfirmProps, EntityLabels, formatReferenceSourceDisplay, ListPageTitles, recordLifecycleListConfirmBodies, resolveActionErrorMessage, showToastSuccess, appRoutes } from "../../utils";
import {
    buildSaAdminPagedListActions,
    getSaAdminActionSuccessMessage,
    type SaAdminConfirmKind,
} from "./saAdminListActions";

type ConfirmKind = SaAdminConfirmKind | null;

const SourceActivitiesListPage = () => {
    const navigate = useNavigate();

    const [refreshNumber, setRefreshNumber] = useState(0);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [confirmSaId, setconfirmSaId] = useState<string | null>(null);
    const [confirmSaLabel, setconfirmSaLabel] = useState("");

    const closeConfirm = () => {
        setConfirmKind(null);
        setconfirmSaId(null);
        setconfirmSaLabel("");
    };

    const runConfirmedAction = () => {
        if (!confirmSaId || !confirmKind) return;
        const id = confirmSaId;
        const op =
            confirmKind === "disable"
                ? sourceActivitiesService.disable(id)
                : confirmKind === "enable"
                  ? sourceActivitiesService.enable(id)
                  : confirmKind === "softDelete"
                    ? sourceActivitiesService.softDelete(id)
                    : sourceActivitiesService.restore(id);
        op.then(() => {
            showToastSuccess(getSaAdminActionSuccessMessage(confirmKind, confirmSaLabel));
            setActionError(null);
            setRefreshNumber((n) => n + 1);
            closeConfirm();
        }).catch((e: unknown) => {
            setActionError(resolveActionErrorMessage(e));
            closeConfirm();
        });
    };

    const openConfirm = (kind: ConfirmKind, item: { id: string; code: string }) => {
        setConfirmKind(kind);
        setconfirmSaId(item.id);
        setconfirmSaLabel(item.code);
    };

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.sourceActivity,
                confirmSaLabel,
                recordLifecycleListConfirmBodies(confirmSaLabel)
            ),
        [confirmKind, confirmSaLabel]
    );

    const columns: IColumn[] = useMemo(
        () => [
            {
                member: "code",
                renderAs: ColumnRender.customRenderer,
                title: "Code",
                width: 120,
                customRenderer: (item: { code?: string }) => (
                    <td className="text-start">
                        <span className="font-monospace">{item.code ?? ""}</span>
                    </td>
                ),
            } as IColumn,
            { member: "description", renderAs: ColumnRender.text, title: "Description", width: 320 } as IColumn,
            {
                member: "source",
                renderAs: ColumnRender.customRenderer,
                title: "Source",
                width: 120,
                customRenderer: (item: { source?: string }) => (
                    <td className="text-start">{formatReferenceSourceDisplay(item.source)}</td>
                ),
            } as IColumn,
            { member: "isActive", renderAs: ColumnRender.checkCross, title: "Active", width: 90, align: "center" } as IColumn,
        ],
        []
    );

    const filters: IFilter[] = useMemo(
        () => [
            { title: "Code", member: "code", type: FilterType.text } as IFilter,
            { title: "Description", member: "description", type: FilterType.text } as IFilter,
            { title: "Source", member: "source", type: FilterType.text } as IFilter,
            {
                title: "Active",
                member: "isActive",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Deleted",
                member: "isDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter,
        ],
        []
    );

    const itemActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: "Edit",
                onClick: (item: { id: string }) => navigate(`/source-activities/${item.id}`),
                primary: true,
            } as IPagedListAction,
            ...buildSaAdminPagedListActions((kind, item) => openConfirm(kind, item)),
        ],
        [navigate]
    );

    const headActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: addEntityActionLabel(EntityLabels.sourceActivity),
                icon: faPlus,
                onClick: () => navigate("/source-activities/new"),
                primary: true,
            } as IPagedListAction,
        ],
        [navigate]
    );

    return (
        <Container fluid>
            <Helmet>
                <title>{ListPageTitles.sourceActivities}</title>
            </Helmet>
            <Row className="mb-3">
                <Col>
                    <ResponsiveBreadcrumb
                        items={[
                            { text: EntityLabels.referenceData, to: appRoutes.codes },
                            { text: ListPageTitles.sourceActivities },
                        ]}
                    />
                    <h1>{ListPageTitles.sourceActivities}</h1>
                    <p className="text-muted">
                        Source activity values for regulatory reporting. Administrators can add, edit, disable, or remove source activities.
                    </p>
                    {actionError && (
                        <Alert variant="danger" className="mt-2" onClose={() => setActionError(null)} dismissible>
                            {actionError}
                        </Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    <PagedList
                        columns={columns}
                        filters={filters}
                        headActions={headActions}
                        itemActions={itemActions}
                        rowActionsAriaLabel="Source activity actions"
                        showDeletedRowIndicator
                        defaultSortBy="Code"
                        defaultSortOrder={SortOrder.ascending}
                        getUrl="/api/referencedata/source-activities/all"
                        rowFormatter={() => null}
                        persistanceKey="SourceActivitiesListPage"
                        refreshNumber={refreshNumber}
                    />
                </Col>
            </Row>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runConfirmedAction();
                    else closeConfirm();
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { SourceActivitiesListPage };


